import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import ChatInterface from './components/ChatInterface';
import Sidebar from './components/Sidebar';
import Settings from './components/Settings';
import Login from './components/Login';
import InstallPrompt from './components/InstallPrompt';
import UpdatePrompt from './components/UpdatePrompt';
import WelcomeSplash from './components/WelcomeSplash';
import FilesManager from './components/FilesManager';
import AppsPage from './components/AppsPage';
import SystemPrompt from './components/SystemPrompt';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SessionProvider } from './contexts/SessionContext';
import { SocketProvider } from './contexts/SocketContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { AppearanceProvider } from './contexts/AppearanceContext';
import { NewFeaturesProvider } from './contexts/NewFeaturesContext';
import { MCPProvider } from './contexts/MCPContext';
import { SeasonalThemeProvider } from './contexts/SeasonalThemeContext';
import { fetchFilteredModels } from './services/api';
import { fetchConversations, fetchConversation } from './services/conversationApi';
import { getAppConversation, createAppConversation } from './services/appsApi';
import { getContextSpaceContext, getContextSpace, addConversationToContextSpace } from './services/contextSpacesApi';
import ResourceViewer from './components/ResourceViewer';
import GuidedTour from './components/GuidedTour';
import { AUTH_BASE_URL } from './config/api';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { initVersionCheck } from './utils/versionCheck';
import { SeasonalDecorations } from './components/seasonal/SeasonalDecorations';

function AppContent() {
  const [models, setModels] = useState({ openai: [], anthropic: [] });
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [updateVersion, setUpdateVersion] = useState(null);
  const [conversationModels, setConversationModels] = useState({});
  const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);
  const [showFilesManager, setShowFilesManager] = useState(false);
  const [showAIApps, setShowAIApps] = useState(false);
  const [showContextSpaces, setShowContextSpaces] = useState(false);
  const [showAppsPage, setShowAppsPage] = useState(false);
  const [showSystemPromptModal, setShowSystemPromptModal] = useState(false);
  const [allConversationsLoaded, setAllConversationsLoaded] = useState(false);
  const [selectedContextSpaceId, setSelectedContextSpaceId] = useState(null);
  const [contextSpaceContext, setContextSpaceContext] = useState(null);
  const [viewingResource, setViewingResource] = useState(null); // { contextSpaceId, resourceId }
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [conversationsCursor, setConversationsCursor] = useState(null); // For pagination
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const { user, loading, authError } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const loadingRef = useRef(false);
  const initialLoadRef = useRef(false);

  // Move createNewConversation before useEffect to avoid hoisting issues
  const createNewConversation = useCallback(async () => {
    // Always create a local conversation first
    const newConversation = {
      id: 'temp-' + Date.now().toString(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date(),
      isLocal: true  // Mark as local/unsaved
    };
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversationId(newConversation.id);
    
    // If we're in a context space, stay in the context space
    if (selectedContextSpaceId) {
      // Stay in the context space URL - don't navigate away
      navigate(`/context-space/${selectedContextSpaceId}`);
    } else {
      // Navigate to home for new conversations (welcome screen)
      navigate('/');
    }
  }, [navigate, selectedContextSpaceId]);

  useEffect(() => {
    const loadData = async () => {
      // Prevent duplicate calls
      if (loadingRef.current) {
        return;
      }
      
      loadingRef.current = true;
      
      try {
        
        // Load models filtered by user permissions
        const modelData = await fetchFilteredModels();
        setModels(modelData);
        
        // Check if new version splash hasn't been shown
        const v220WelcomeSeen = localStorage.getItem('v220WelcomeSeen');

        if (!v220WelcomeSeen && user) {
          setShowWelcomeSplash(true);
        }

        // Load conversations if user is authenticated
        if (user) {
          // Check if we have a context space ID from the URL first (works regardless of conversations)
          const contextSpaceIdFromUrl = params.contextSpaceId;
          
          if (contextSpaceIdFromUrl) {
            // Load context space from URL
            try {
              // Expand sidebar when loading context space
              setSidebarCollapsed(false);
              
              // Use the proper handleSelectContextSpace function which filters conversations correctly
              await handleSelectContextSpace(contextSpaceIdFromUrl, true);
              
              setAllConversationsLoaded(true);
              return; // Exit early - handleSelectContextSpace handles conversation loading and filtering
            } catch (error) {
              console.error('Failed to load context space from URL:', error);
              // If loading fails, navigate to home and continue with normal flow
              navigate('/');
            }
          }
          
          // Load limited conversations initially (3 for collapsed sidebar, 50 for expanded)
          const limit = sidebarCollapsed ? 3 : 50;
          const response = await fetchConversations(limit);

          // Handle paginated response - ensure userConversations is always an array
          const userConversations = Array.isArray(response?.conversations)
            ? response.conversations
            : Array.isArray(response)
              ? response
              : [];
          const nextCursor = response?.nextCursor || null;
          const hasMore = response?.hasMore || false;

          setConversations(userConversations);
          setConversationsCursor(nextCursor);
          setHasMoreConversations(hasMore);
          setAllConversationsLoaded(!hasMore); // Track if we loaded all

          if (userConversations.length > 0) {

            // Check if we have a conversation ID from the URL
            const conversationIdFromUrl = params.conversationId;

            if (conversationIdFromUrl) {
              // Try to load the conversation from URL directly (don't rely on limited list)
              try {
                setCurrentConversationId(conversationIdFromUrl);
                const fullConversation = await fetchConversation(conversationIdFromUrl);

                // Check if conversation is already in list
                const convExists = userConversations.find(c => c.id === conversationIdFromUrl);

                if (convExists) {
                  // Update existing conversation with messages
                  setConversations(prev =>
                    prev.map(conv =>
                      conv.id === conversationIdFromUrl
                        ? { ...conv, messages: fullConversation.messages || [] }
                        : conv
                    )
                  );
                } else {
                  // Add conversation to list if not present (it was outside limited fetch)
                  setConversations(prev => [
                    {
                      id: fullConversation.id,
                      title: fullConversation.title,
                      messages: fullConversation.messages || [],
                      createdAt: fullConversation.createdAt,
                      updatedAt: fullConversation.updatedAt,
                      model: fullConversation.model,
                      metadata: fullConversation.metadata
                    },
                    ...prev
                  ]);
                }

                return; // Exit early, don't create new conversation
              } catch (error) {
                console.error('Failed to load conversation from URL:', error);
                // If loading fails (401/404/etc), navigate to home
                navigate('/');
                // Create a simple new conversation without the callback
                const newConversation = {
                  id: 'temp-' + Date.now().toString(),
                  title: 'New Chat',
                  messages: [],
                  createdAt: new Date(),
                  isLocal: true
                };
                setConversations(prev => [newConversation, ...prev]);
                setCurrentConversationId(newConversation.id);
                return;
              }
            } else {
              // No conversation ID in URL, show welcome screen
              // Don't create a conversation automatically - let user start one when they're ready
              setCurrentConversationId(null);
            }
          } else {
            // No conversations exist - show welcome screen
            // Let user create their first conversation when ready
            setCurrentConversationId(null);
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        loadingRef.current = false;
      }
    };

    // Prevent infinite loops by checking if already loaded
    if (!loading && user !== undefined && !initialLoadRef.current && !loadingRef.current) {
      initialLoadRef.current = true;
      loadData();
    }
  }, [user, loading]); // Remove params.conversationId to prevent reload loops

  // Helper function to generate initial prompt based on app type
  const getInitialPromptForApp = (appData) => {
    const options = appData.metadata?.options || {};
    
    switch (appData.appId) {
      case 'draft-composer':
        if (options.emailThread) {
          return 'Please compose a draft based on the context provided.';
        }
        return 'Please help me compose a draft.';
      
      case 'prd-generator':
        return 'Please create a product requirements document based on the specifications provided.';
      
      case 'competitor-analyzer':
        return 'Please analyze the competitive landscape based on the information provided.';
      
      case 'website-auditor':
        if (options.websiteUrl) {
          return `Please audit the website: ${options.websiteUrl}`;
        }
        return 'Please help me audit a website.';
      
      case 'code-reviewer':
        return 'Please review the code I provide.';
      
      case 'brainstorm-partner':
        if (options.topic) {
          return `Let's brainstorm about: ${options.topic}`;
        }
        return 'Let\'s start brainstorming.';
      
      default:
        return 'How can I help you today?';
    }
  };

  // Define app conversation handlers
  const handleNewAppConversation = useCallback(async (appData) => {
    console.log('Creating new app conversation with data:', appData);
    try {
      const conversation = await createAppConversation({
        appId: appData.appId,
        appName: appData.appName,
        systemPrompt: appData.systemPrompt,
        metadata: appData.metadata
      });
      
      console.log('Created conversation:', conversation);
      
      // Add to conversations list
      setConversations(prev => [conversation, ...prev]);
      setCurrentConversationId(conversation.id);
      
      // Store auto-start info in session storage to persist across navigation
      sessionStorage.setItem(`autoStart_${conversation.id}`, JSON.stringify({
        autoStart: true,
        initialPrompt: getInitialPromptForApp(appData)
      }));
      
      // Navigate to the new conversation
      navigate(`/apps/conversation/${conversation.id}`);
    } catch (error) {
      console.error('Failed to create app conversation:', error);
    }
  }, [navigate]);

  const loadAppConversation = useCallback(async (conversationId) => {
    try {
      const conversation = await getAppConversation(conversationId);
      
      // Add to conversations if not already there
      setConversations(prev => {
        const exists = prev.find(c => c.id === conversationId);
        if (!exists) {
          return [conversation, ...prev];
        }
        return prev.map(c => c.id === conversationId ? conversation : c);
      });
      
      setCurrentConversationId(conversationId);
    } catch (error) {
      console.error('Failed to load app conversation:', error);
      navigate('/apps');
    }
  }, [navigate]);

  // Handle app routes
  useEffect(() => {
    console.log('Location changed:', location.pathname, location.state);
    if (location.pathname === '/apps') {
      setShowAppsPage(true);
      setShowFilesManager(false);
    } else if (location.pathname === '/files') {
      setShowFilesManager(true);
      setShowAppsPage(false);
    } else if (location.pathname.startsWith('/apps/conversation/')) {
      setShowAppsPage(false);
      setShowFilesManager(false);
      // Handle app conversation loading
      const appConversationId = location.pathname.split('/').pop();
      console.log('App conversation ID:', appConversationId);
      if (appConversationId === 'new' && location.state) {
        // Create new app conversation
        console.log('Creating new app conversation with state:', location.state);
        handleNewAppConversation(location.state);
      } else if (appConversationId && appConversationId !== 'new') {
        // Load existing app conversation
        console.log('Loading existing app conversation:', appConversationId);
        loadAppConversation(appConversationId);
      }
    } else {
      setShowAppsPage(false);
      setShowFilesManager(false);
    }
  }, [location, handleNewAppConversation, loadAppConversation]);

  // Handle window resize for responsive left sidebar
  useEffect(() => {
    const handleResize = () => {
      // Only auto-collapse on mobile screens
      if (window.innerWidth <= 768) {
        setSidebarCollapsed(true);
      }
      // Don't auto-expand on larger screens - let user control it
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Register service worker and handle updates
  useEffect(() => {
    const swConfig = {
      onUpdate: (registration) => {
        console.log('Update available, showing prompt');
        serviceWorkerRegistration.getAppVersion().then(version => {
          setUpdateVersion(version);
          setShowUpdatePrompt(true);
        });
      },
      onSuccess: (registration) => {
        console.log('Service worker registered successfully');
      }
    };

    serviceWorkerRegistration.register(swConfig);
    
    // Also start version checking for PWAs
    initVersionCheck();
  }, []);

  const handleUpdateApp = () => {
    setShowUpdatePrompt(false);
    serviceWorkerRegistration.forceUpdate();
  };

  const handleDismissUpdate = () => {
    setShowUpdatePrompt(false);
  };
  
  const handleToggleSidebar = async () => {
    const newCollapsedState = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsedState);
    
    // If expanding the sidebar and user is authenticated, load all conversations
    // Only fetch if we haven't loaded initial conversations yet
    if (!newCollapsedState && user && conversations.length === 0) {
      try {
        // Remember if we were on welcome screen (no conversation selected)
        const wasOnWelcomeScreen = currentConversationId === null;

        const response = await fetchConversations(50);
        const userConversations = response.conversations || response;
        const nextCursor = response.nextCursor || null;
        const hasMore = response.hasMore || false;

        setConversations(userConversations);
        setConversationsCursor(nextCursor);
        setHasMoreConversations(hasMore);
        setAllConversationsLoaded(!hasMore);

        // If we were on welcome screen, explicitly keep currentConversationId as null
        if (wasOnWelcomeScreen) {
          setCurrentConversationId(null);
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    }
  };

  // Refresh conversations from server
  const refreshConversations = async () => {
    if (!user) return;
    try {
      const response = await fetchConversations(50);
      // Ensure userConversations is always an array
      const userConversations = Array.isArray(response?.conversations)
        ? response.conversations
        : Array.isArray(response)
          ? response
          : [];
      const nextCursor = response?.nextCursor || null;
      const hasMore = response?.hasMore || false;

      setConversations(userConversations);
      setConversationsCursor(nextCursor);
      setHasMoreConversations(hasMore);
      setAllConversationsLoaded(!hasMore);
    } catch (error) {
      console.error('Failed to refresh conversations:', error);
    }
  };

  // Load more conversations (for infinite scroll)
  const loadMoreConversations = async () => {
    if (!user || !hasMoreConversations || !conversationsCursor) return;

    try {
      const response = await fetchConversations(50, conversationsCursor);
      // Ensure moreConversations is always an array
      const moreConversations = Array.isArray(response?.conversations)
        ? response.conversations
        : Array.isArray(response)
          ? response
          : [];
      const nextCursor = response?.nextCursor || null;
      const hasMore = response?.hasMore || false;

      // Append new conversations to existing ones
      setConversations(prev => [...prev, ...moreConversations]);
      setConversationsCursor(nextCursor);
      setHasMoreConversations(hasMore);
      setAllConversationsLoaded(!hasMore);
    } catch (error) {
      console.error('Failed to load more conversations:', error);
    }
  };

  // Handle Context Space selection
  const handleSelectContextSpace = async (contextSpaceId, skipNavigation = false) => {
    if (!contextSpaceId) {
      setSelectedContextSpaceId(null);
      setContextSpaceContext(null);
      // Always clear current conversation when exiting a context space to show welcome screen
      setCurrentConversationId(null);
      
      // Navigate to home when deselecting context space (unless we're skipping navigation)
      if (!skipNavigation) {
        navigate('/');
        // Reload initial conversations
        const response = await fetchConversations(50);
        const userConversations = response.conversations || response;
        const nextCursor = response.nextCursor || null;
        const hasMore = response.hasMore || false;

        setConversations(userConversations);
        setConversationsCursor(nextCursor);
        setHasMoreConversations(hasMore);
        setAllConversationsLoaded(!hasMore);
      }
      return;
    }

    try {
      setSelectedContextSpaceId(contextSpaceId);
      
      // Load the context space with its conversations and context
      const [context, contextSpace] = await Promise.all([
        getContextSpaceContext(contextSpaceId),
        getContextSpace(contextSpaceId)
      ]);
      
      setContextSpaceContext(context);
      
      // Filter conversations to only show those in this context space
      const contextSpaceConversations = contextSpace.conversations?.map(item => item.conversation) || [];
      setConversations(contextSpaceConversations);
      
      // If we have conversations, select the first one, otherwise create a new one
      if (contextSpaceConversations.length > 0) {
        setCurrentConversationId(contextSpaceConversations[0].id);
      } else {
        // Create a new conversation for this context space
        const newConversation = {
          id: 'temp-' + Date.now().toString(),
          title: 'New Chat',
          messages: [],
          createdAt: new Date(),
          isLocal: true
        };
        setConversations([newConversation]);
        setCurrentConversationId(newConversation.id);
      }
      
      // Navigate to context space URL (unless we're skipping navigation, e.g., when loading from URL)
      if (!skipNavigation) {
        navigate(`/context-space/${contextSpaceId}`);
      }
      
      console.log('Context Space selected:', contextSpaceId, 'Context:', context);
    } catch (error) {
      console.error('Failed to load context space context:', error);
      setSelectedContextSpaceId(null);
      setContextSpaceContext(null);
      throw error; // Re-throw to handle in URL loading
    }
  };

  // Handle resource viewing
  const handleViewResource = (contextSpaceId, resourceId, resourceData = null) => {
    if (contextSpaceId && resourceId) {
      setViewingResource({ contextSpaceId, resourceId, resourceData });
    } else {
      setViewingResource(null);
    }
  };

  const handleBackFromResource = () => {
    setViewingResource(null);
  };

  const handleStartGuidedTour = () => {
    setShowGuidedTour(true);
  };

  const handleCompleteGuidedTour = () => {
    setShowGuidedTour(false);
    localStorage.setItem('guidedTourCompleted', 'true');
  };
  
  const handleModelChange = (conversationId, model) => {
    if (conversationId) {
      setConversationModels(prev => ({
        ...prev,
        [conversationId]: model
      }));
    }
  };

  // Remove this useEffect as it conflicts with URL-based loading
  // useEffect(() => {
  //   if (!loading && conversations.length === 0 && !currentConversationId) {
  //     createNewConversation();
  //   }
  // }, [loading, conversations.length, currentConversationId]);

  const currentConversation = conversations.find(conv => conv.id === currentConversationId) || null;
  
  // Debug logging for conversation state
  useEffect(() => {
    if (currentConversation) {
      console.log('🚀 App.js currentConversation CHANGED via useEffect:', {
        id: currentConversation.id, 
        messagesCount: currentConversation.messages?.length || 0,
        lastMessage: currentConversation.messages?.[currentConversation.messages?.length - 1],
        conversationReference: currentConversation,
        messagesArrayReference: currentConversation.messages
      });
    } else {
      console.log('🚀 App.js currentConversation is null');
    }
  }, [currentConversation]);

  const updateConversation = async (conversationId, updates) => {
    console.log('🚀 App.js updateConversation called:', { conversationId, updatesType: typeof updates, messagesLength: updates?.messages?.length });
    
    // Handle special case where ChatInterface requests creating a new conversation for voice messages
    if (conversationId === 'CREATE_NEW') {
      console.log('🚀 Creating new conversation for voice message in App.js');
      // Create a new local conversation
      const newConversation = {
        id: 'temp-' + Date.now().toString(),
        title: 'New Chat',
        messages: updates.messages || [],
        createdAt: new Date(),
        isLocal: true
      };
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(newConversation.id);
      
      // Navigate to home to show the new conversation
      if (selectedContextSpaceId) {
        navigate(`/context-space/${selectedContextSpaceId}`);
      } else {
        navigate('/');
      }
      
      console.log('🚀 Created new conversation with ID:', newConversation.id, 'and messages:', updates.messages);
      return newConversation.id;
    }
    
    let conversation = conversations.find(c => c.id === conversationId);
    console.log('🚀 Found conversation:', !!conversation, 'ID:', conversationId);
    
    // If this is a local conversation and we're adding the first message, save it to the server
    if (conversation?.isLocal && updates.messages?.length > 0 && user) {
      try {
        const response = await fetch(`${AUTH_BASE_URL}/api/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title: updates.title || conversation.title }),
        });
        const savedConversation = await response.json();
        
        // Replace the temporary conversation with the saved one
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId 
              ? { ...savedConversation, ...updates, isLocal: false }
              : conv
          )
        );
        
        // Update the current conversation ID to the new one
        setCurrentConversationId(savedConversation.id);
        
        // Transfer model selection from temp ID to new ID
        if (conversationModels[conversationId]) {
          setConversationModels(prev => {
            const newModels = { ...prev };
            newModels[savedConversation.id] = newModels[conversationId];
            delete newModels[conversationId];
            return newModels;
          });
        }
        
        // Update URL to reflect the new conversation ID
        if (selectedContextSpaceId) {
          // Stay in context space if we're in one
          navigate(`/context-space/${selectedContextSpaceId}`);
          
          // Add the conversation to the current context space
          try {
            await addConversationToContextSpace(selectedContextSpaceId, savedConversation.id);
            console.log(`Conversation ${savedConversation.id} added to context space ${selectedContextSpaceId}`);
            
            // Refresh conversations to show the context space icon immediately
            await refreshConversations();
          } catch (error) {
            console.error('Failed to add conversation to context space:', error);
          }
        } else {
          navigate(`/conversation/${savedConversation.id}`);
        }
        
        // Continue with the update using the new ID
        conversationId = savedConversation.id;
        conversation = savedConversation;
        
        // Return the new conversation ID
        return savedConversation.id;
      } catch (error) {
        console.error('Failed to save conversation:', error);
      }
    }
    
    // For existing conversations, update locally and save to server
    console.log('🚀 Updating conversation locally. Current messages:', conversation?.messages?.length, 'New messages:', updates?.messages?.length);
    
    // Handle case where updates.messages is a single message (for voice messages)
    let finalUpdates = updates;
    
    setConversations(prev => {
      if (updates.messages && !Array.isArray(updates.messages)) {
        console.log('🚀 Single message detected, appending to conversation');
        // Get current messages from the latest state to ensure we have the most recent ones
        const currentConversation = prev.find(c => c.id === conversationId);
        const currentMessages = currentConversation?.messages || [];
        console.log('🚀 Current conversation messages count (from state):', currentMessages.length);
        finalUpdates = {
          ...updates,
          messages: [...currentMessages, updates.messages]
        };
        console.log('🚀 After appending single message, total count:', finalUpdates.messages.length);
      }
      
      const updated = prev.map(conv => {
        if (conv.id === conversationId) {
          const updatedConv = { ...conv, ...finalUpdates };
          // Sort messages by timestamp to ensure proper chronological order
          if (updatedConv.messages && Array.isArray(updatedConv.messages)) {
            updatedConv.messages = updatedConv.messages.sort((a, b) => {
              const timeA = new Date(a.timestamp || a.createdAt || 0).getTime();
              const timeB = new Date(b.timestamp || b.createdAt || 0).getTime();
              return timeA - timeB;
            });
            console.log('🚀 Messages sorted by timestamp for proper order');
          }
          return updatedConv;
        }
        return conv;
      });
      console.log('🚀 Conversations state updated. New conversation messages count:', updated.find(c => c.id === conversationId)?.messages?.length);
      return updated;
    });
    
    // Save to server if messages are being updated
    if (updates.messages && !conversation?.isLocal && user) {
      try {
        await fetch(`${AUTH_BASE_URL}/api/conversations/${conversationId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(finalUpdates),
        });
      } catch (error) {
        console.error('Failed to save messages:', error);
      }
    }
    
    // Return the current conversation ID (may have changed if it was temporary)
    return conversationId;
  };

  const handleSelectConversation = async (conversationId) => {
    setCurrentConversationId(conversationId);
    
    // Update URL to reflect selected conversation
    navigate(`/conversation/${conversationId}`);
    
    // Always load full conversation history for non-local conversations
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation && !conversation.isLocal) {
      try {
        const fullConversation = await fetchConversation(conversationId);
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, messages: fullConversation.messages || [] }
              : conv
          )
        );
      } catch (error) {
        console.error('Failed to load conversation history:', error);
      }
    }
  };

  const handleDeleteConversation = (conversationId) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    // If we're deleting the current conversation, navigate appropriately
    if (conversationId === currentConversationId) {
      const remainingConvs = conversations.filter(conv => conv.id !== conversationId);
      if (remainingConvs.length > 0) {
        const newCurrentId = remainingConvs[0].id;
        setCurrentConversationId(newCurrentId);
        navigate(`/conversation/${newCurrentId}`);
      } else {
        setCurrentConversationId(null);
        navigate('/');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Show error if auth check failed
  if (authError) {
    const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.startsWith('192.168');

    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
              {isProduction ? 'Service Temporarily Unavailable' : 'Connection Error'}
            </h2>

            {/* Message */}
            <p className="text-gray-600 text-center mb-6">
              {isProduction
                ? "We're experiencing technical difficulties. Our team has been notified and is working to resolve this issue."
                : "Unable to connect to the server. Please ensure the backend service is running."
              }
            </p>

            {/* Technical Details (Development Only) */}
            {!isProduction && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Technical Details:</p>
                <p className="text-sm text-gray-600 font-mono break-all">{authError}</p>
                <p className="text-xs text-gray-500 mt-2">Backend should be running on port 4001</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>

              {isProduction && (
                <a
                  href="https://stats.uptimerobot.com/aFPthL05Lx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                >
                  Check System Status
                </a>
              )}
            </div>

            {/* Support Link */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Need help? <a href="mailto:support@supple.tools" className="text-blue-600 hover:text-blue-700 font-medium">Contact Support</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <Login />;
  }

  return (
    <div className="flex h-screen bg-claude-bg w-full overflow-hidden relative">
      {/* Seasonal Theme Decorations */}
      <SeasonalDecorations location="global" />

      <Sidebar
        conversations={conversations}
        currentConversationId={currentConversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={createNewConversation}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        onOpenSettings={() => setShowSettings(true)}
        onDeleteConversation={handleDeleteConversation}
        onOpenFiles={() => navigate('/files')}
        onOpenApps={() => setShowAIApps(true)}
        onShowContextSpaces={() => setShowContextSpaces(true)}
        onUpdateConversation={updateConversation}
        onOpenMemory={() => setShowSystemPromptModal(true)}
        onSelectContextSpace={handleSelectContextSpace}
        onViewResource={handleViewResource}
        selectedContextSpaceId={selectedContextSpaceId}
        onRefreshConversations={refreshConversations}
        onStartGuidedTour={handleStartGuidedTour}
        onLoadMoreConversations={loadMoreConversations}
        hasMoreConversations={hasMoreConversations}
      />
      <div className="flex-1 flex flex-col min-w-0 w-full md:w-auto">
        {showAppsPage ? (
          <AppsPage onBack={() => {
            setShowAppsPage(false);
            navigate('/');
          }} />
        ) : showFilesManager ? (
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Files Manager</h2>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Back to Chat
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <FilesManager />
            </div>
          </div>
        ) : (
          <ChatInterface 
            models={models}
            conversation={currentConversation}
            onUpdateConversation={updateConversation}
            onToggleSidebar={handleToggleSidebar}
            sidebarCollapsed={sidebarCollapsed}
            currentUser={user}
            selectedModel={conversationModels[currentConversationId]}
            onModelChange={(model) => handleModelChange(currentConversationId, model)}
            showAIApps={showAIApps}
            setShowAIApps={setShowAIApps}
            showContextSpaces={showContextSpaces}
            setShowContextSpaces={setShowContextSpaces}
            onNewConversation={createNewConversation}
            isAppConversation={location.pathname.startsWith('/apps/conversation/')}
            onOpenMemory={() => setShowSystemPromptModal(true)}
            selectedContextSpaceId={selectedContextSpaceId}
            contextSpaceContext={contextSpaceContext}
            onExitContextSpace={() => handleSelectContextSpace(null)}
            viewingResource={viewingResource}
            onCloseResourcePreview={handleBackFromResource}
          />
        )}
      </div>
      
      
      {showSettings && (
        <Settings 
          models={models}
          currentUser={user}
          onClose={() => setShowSettings(false)}
        />
      )}
      <InstallPrompt />
      <UpdatePrompt 
        isVisible={showUpdatePrompt}
        onUpdate={handleUpdateApp}
        onDismiss={handleDismissUpdate}
        version={updateVersion}
      />
      {showWelcomeSplash && (
        <WelcomeSplash onClose={() => {
          setShowWelcomeSplash(false);
          localStorage.setItem('v220WelcomeSeen', 'true');
        }} />
      )}
      
      {/* Global Memory Modal */}
      <SystemPrompt
        isOpen={showSystemPromptModal}
        onClose={() => setShowSystemPromptModal(false)}
      />

      {/* Guided Tour */}
      <GuidedTour
        isActive={showGuidedTour}
        onClose={() => setShowGuidedTour(false)}
        onComplete={handleCompleteGuidedTour}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppearanceProvider>
        <NewFeaturesProvider>
          <SessionProvider>
            <PermissionsProvider>
              <MCPProvider>
                <SeasonalThemeProvider>
                  <SocketProvider>
                    <Router>
                    <Routes>
                      <Route path="/" element={<AppContent />} />
                      <Route path="/conversation/:conversationId" element={<AppContent />} />
                      <Route path="/context-space/:contextSpaceId" element={<AppContent />} />
                      <Route path="/files" element={<AppContent />} />
                      <Route path="/apps" element={<AppContent />} />
                      <Route path="/apps/conversation/:conversationId" element={<AppContent />} />
                    </Routes>
                    </Router>
                  </SocketProvider>
                </SeasonalThemeProvider>
              </MCPProvider>
            </PermissionsProvider>
          </SessionProvider>
        </NewFeaturesProvider>
      </AppearanceProvider>
    </AuthProvider>
  );
}

export default App;